import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useWebEDIStore } from 'stores/webedi/webedi';
import portalSchema from 'support/types/schema-portal';
import { Popup } from 'components/Display/Popup/Popup';
import { Button } from 'components/Form/Button/Button';
import { Checkbox } from 'components/Form/Checkbox/Checkbox';
import { MasterdataLink } from 'components/WebEDI/FieldAnnotationPopover/components/MasterdataLink/MasterdataLink';

import { useUnmatchedArticleDisplayData } from '../helpers/useUnmatchedArticleDisplayData';

type Annotation = portalSchema.components['schemas']['Annotation'];

type Props = {
  open: boolean;
  annotations: Array<Annotation>;
  onConfirm: (checkedIds: Array<string>) => void;
  onCancel: () => void;
};

export const UnmatchedArticleConfirmationModal: FC<Props> = ({ open, annotations, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [checkedIds, setCheckedIds] = useState<Set<string>>(() => {
    const toAdd = useWebEDIStore.getState().articlesToAddToMasterdata;
    return new Set(annotations.filter((annotation) => toAdd.has(annotation.id)).map((annotation) => annotation.id));
  });

  const allChecked = annotations.length > 0 && annotations.every((annotation) => checkedIds.has(annotation.id));
  const someChecked = annotations.some((annotation) => checkedIds.has(annotation.id));
  const checkedCount = checkedIds.size;

  const toggleOne = (annotationId: string) =>
    setCheckedIds((previous) => {
      const next = new Set(previous);
      if (next.has(annotationId)) {
        next.delete(annotationId);
      } else {
        next.add(annotationId);
      }
      return next;
    });

  const toggleAll = () =>
    setCheckedIds(() => (allChecked ? new Set() : new Set(annotations.map((annotation) => annotation.id))));

  const handleConfirm = () =>
    onConfirm(annotations.filter((annotation) => checkedIds.has(annotation.id)).map((annotation) => annotation.id));

  const CancelButton = () => (
    <Button variant="secondary" onClick={onCancel} analyticsId="webedi:unmatched-article-confirmation-cancel">
      {t('common:cancel')}
    </Button>
  );

  const PrimaryButton = () => (
    <Button variant="primary" onClick={handleConfirm} analyticsId="webedi:unmatched-article-confirmation-confirm">
      {checkedCount === 0
        ? t('webedi:unmatchedArticleConfirmation.primaryEmpty')
        : t('webedi:unmatchedArticleConfirmation.primary', { count: checkedCount })}
    </Button>
  );

  return (
    <Popup
      open={open}
      onClose={onCancel}
      title={t('webedi:unmatchedArticleConfirmation.title')}
      subtitle={t('webedi:unmatchedArticleConfirmation.description')}
      CancelButton={CancelButton}
      PrimaryButton={PrimaryButton}
    >
      <div className="flex flex-col gap-4">
        <MasterdataLink label={t('webedi:unmatchedArticleConfirmation.viewMasterdata')} />

        <label className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
          <Checkbox
            checked={allChecked}
            indeterminate={!allChecked && someChecked}
            onChange={toggleAll}
            aria-label={t('webedi:unmatchedArticleConfirmation.selectAll')}
          />
          <span className="text-sm font-medium text-gray-700">
            {t('webedi:unmatchedArticleConfirmation.selectAll')}
          </span>
        </label>

        <div className="divide-y divide-gray-200 rounded-md border border-gray-200">
          {annotations.map((annotation) => (
            <UnmatchedArticleRow
              key={annotation.id}
              annotation={annotation}
              checked={checkedIds.has(annotation.id)}
              onToggle={() => toggleOne(annotation.id)}
            />
          ))}
        </div>
      </div>
    </Popup>
  );
};

type RowProps = {
  annotation: Annotation;
  checked: boolean;
  onToggle: () => void;
};

const UnmatchedArticleRow = ({ annotation, checked, onToggle }: RowProps) => {
  const { t } = useTranslation();
  const { name, gtin } = useUnmatchedArticleDisplayData(annotation);
  const displayName = name || gtin || '';

  return (
    <label className="flex items-center gap-3 p-3">
      <Checkbox checked={checked} onChange={onToggle} aria-label={displayName} />
      <span className="flex flex-col">
        <span className="text-sm leading-5 text-gray-900">{displayName}</span>
        {gtin ? (
          <span className="text-xs leading-4 text-gray-500">
            {t('webedi:unmatchedArticleConfirmation.gtin', { gtin })}
          </span>
        ) : null}
      </span>
    </label>
  );
};
